import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, Heart, MessageCircle, MapPin, Eye, Sparkles, TrendingUp, Award, Gem, Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { favoritesApi } from '../services/api';
import { getCategoryIcon } from '../lib/categoryIcons';
import { buildWhatsAppMessage, normalizeWhatsAppNumber } from '../utils/whatsappLink';
import BackendImage from './BackendImage';

/* ---------------- helpers ---------------- */
const norm = (s) => (s || '').toString().normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-+|-+$/g, '');

/**
 * Compute open-now status from `horario_funcionamento`.
 * Supports two shapes:
 *   1. Array: [{ dia: 'segunda'|'mon'|0, abre: 'HH:mm', fecha: 'HH:mm' }]
 *   2. Map:   { segunda: { abre, fecha } }
 * Returns: { open: boolean, label: string } | null
 */
function computeOpenStatus(horario) {
  if (!horario) return null;
  const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
  const now = new Date();
  const todayKey = days[now.getDay()];
  let entry = null;
  if (Array.isArray(horario)) {
    entry = horario.find(h => norm(h.dia) === todayKey || norm(h.dia_semana) === todayKey);
  } else if (typeof horario === 'object') {
    entry = horario[todayKey] || horario[todayKey.replace('terca', 'terça').replace('sabado', 'sábado')];
  }
  if (!entry) return null;
  const abre = entry.abre || entry.opens || entry.open;
  const fecha = entry.fecha || entry.closes || entry.close;
  if (!abre || !fecha) return null;
  const [ah, am] = String(abre).split(':').map(Number);
  const [fh, fm] = String(fecha).split(':').map(Number);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = ah * 60 + (am || 0);
  const closeMin = fh * 60 + (fm || 0);
  const isOpen = nowMin >= openMin && nowMin < closeMin;
  return {
    open: isOpen,
    label: isOpen ? `Aberto · fecha às ${fecha}h` : `Fechado · abre ${abre}h`,
  };
}

const LEVEL_BADGES = {
  3: { label: 'Perfil Estruturado', icon: Award, cls: 'bg-emerald-500/95 text-white' },
  4: { label: 'Em Destaque', icon: Crown, cls: 'bg-amber-500/95 text-white' },
  5: { label: 'Referência Local', icon: Gem, cls: 'bg-purple-600/95 text-white' },
};

const CATEGORY_PLACEHOLDERS = {}; // legado — agora usamos getCategoryIcon (padrão da plataforma)

function getSessionId() {
  try {
    let sid = localStorage.getItem('ec_session_id');
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('ec_session_id', sid);
    }
    return sid;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
}

/**
 * DiscoveryCard — card unificado para os 4 carrosséis de descoberta.
 * Inclui badges de nível, status aberto/fechado, faixa de preço, prova social
 * e CTAs Salvar/WhatsApp.
 */
export default function DiscoveryCard({
  business,
  origemId = null,
  tipo = null,
  modo = null,
  municipioId = null,
  showRecommendedBadge = false,
  showEmAltaBadge = false,
  onCardClick,
  onAuthRequired,
}) {
  const navigate = useNavigate();
  const [favLoading, setFavLoading] = useState(false);
  const [isFav, setIsFav] = useState(false);

  const openStatus = useMemo(() => computeOpenStatus(business.horario_funcionamento), [business.horario_funcionamento]);
  const levelBadge = LEVEL_BADGES[business.nivel_irn] || null;
  const cover = business.foto_capa || business.logo_url;
  const PlaceholderIcon = getCategoryIcon(business.categoria_slug || business.categoria);

  const targetUrl = useMemo(() => {
    if (business.municipio_slug && (business.categoria_slug || business.categoria) && business.slug) {
      return `/${business.municipio_slug}/${business.categoria_slug || business.categoria}/${business.slug}`;
    }
    return `/negocio/${business.slug || business.id}`;
  }, [business]);

  const goToBusiness = (e) => {
    if (e?.defaultPrevented) return;
    if (origemId && tipo && tipo !== 'historico') {
      try {
        const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/analytics/recommendation-click`;
        const payload = {
          negocio_origem_id: origemId,
          negocio_destino_id: business.id,
          tipo, modo, municipio_id: municipioId,
          session_id: getSessionId(),
          timestamp: new Date().toISOString(),
        };
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        }
      } catch {/* silent */}
    }
    onCardClick?.(business);
    navigate(targetUrl);
  };

  const handleFav = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setFavLoading(true);
    try {
      if (isFav) {
        await favoritesApi.remove(business.id);
        setIsFav(false);
      } else {
        await favoritesApi.add(business.id);
        setIsFav(true);
        toast.success('Adicionado aos favoritos');
      }
    } catch (err) {
      if (err?.response?.status === 401 && onAuthRequired) {
        onAuthRequired();
      } else {
        toast.error('Não foi possível salvar agora.');
      }
    } finally {
      setFavLoading(false);
    }
  };

  const handleWhatsapp = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const phone = normalizeWhatsAppNumber(business.whatsapp || business.telefone);
    if (!phone) {
      toast.error('Este negócio não tem WhatsApp cadastrado.');
      return;
    }
    const msg = buildWhatsAppMessage({
      business,
      municipio: { slug: business.municipio_slug, nome: business.municipio_nome },
    });
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    if (origemId) {
      try {
        const baseUrl = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/analytics/whatsapp-click`;
        const payload = { negocio_id: business.id, origem_id: origemId, tipo, municipio_id: municipioId };
        if (navigator.sendBeacon) navigator.sendBeacon(baseUrl, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      } catch {/* silent */}
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const viewsLabel = (() => {
    if ((business.views_hoje || 0) >= 5) return `${business.views_hoje} viram hoje`;
    if ((business.views_7d || 0) >= 5) return `${business.views_7d} viram esta semana`;
    return null;
  })();

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={goToBusiness}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToBusiness(e); }}
      data-testid={`disc-card-${business.id}`}
      className="bg-white rounded-2xl border border-[var(--border)] overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer w-full focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-amber-50 to-yellow-100">
        {cover ? (
          <BackendImage src={cover} alt={business.nome} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--primary)]">
            <PlaceholderIcon className="w-16 h-16" strokeWidth={1.4} />
          </div>
        )}

        {levelBadge && (
          <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1 ${levelBadge.cls}`}>
            <levelBadge.icon className="w-3 h-3" />
            {levelBadge.label}
          </div>
        )}

        {openStatus && (
          <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/95 ${openStatus.open ? 'text-emerald-700' : 'text-gray-500'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${openStatus.open ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
            {openStatus.label}
          </div>
        )}

        {business.faixa_preco && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-white/95 text-gray-900">
            {business.faixa_preco}
          </div>
        )}

        {showRecommendedBadge && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-600/95 text-white inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" />Recomendado
          </div>
        )}
        {showEmAltaBadge && !showRecommendedBadge && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-orange-500/95 text-white inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />Em alta
          </div>
        )}
      </div>

      <div className="p-3 space-y-1">
        <h3 className="text-sm font-semibold leading-tight text-[var(--text-primary)] line-clamp-2" title={business.nome}>
          {business.nome}
        </h3>
        <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1 truncate">
          {business.subcategoria && <span className="truncate">{business.subcategoria}</span>}
          {business.subcategoria && business.bairro && <span>·</span>}
          {business.bairro && (
            <span className="inline-flex items-center gap-0.5 truncate">
              <MapPin className="w-3 h-3" />{business.bairro}
            </span>
          )}
        </div>
        {(business.total_avaliacoes || 0) > 0 && (
          <div className="text-xs flex items-center gap-1 text-amber-700">
            <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
            {Number(business.avaliacao_media || 0).toFixed(1)}
            <span className="text-[var(--text-secondary)]"> ({business.total_avaliacoes} aval.)</span>
          </div>
        )}
        {viewsLabel && (
          <div className="text-[11px] text-[var(--text-secondary)] inline-flex items-center gap-1">
            <Eye className="w-3 h-3" />{viewsLabel}
          </div>
        )}
      </div>

      <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleFav}
          disabled={favLoading}
          aria-label="Salvar"
          data-testid={`disc-card-fav-${business.id}`}
          className={`inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${isFav ? 'bg-rose-50 border-rose-200 text-rose-700' : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-rose-300 hover:text-rose-600'}`}
        >
          <Heart className={`w-3.5 h-3.5 ${isFav ? 'fill-current' : ''}`} />
          Salvar
        </button>
        <button
          type="button"
          onClick={handleWhatsapp}
          aria-label="Conversar via WhatsApp"
          data-testid={`disc-card-wa-${business.id}`}
          className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          WhatsApp
        </button>
      </div>
    </article>
  );
}
